import type { Scene } from "@babylonjs/core/scene";

/** Everything a block of code added to the scene, removed whole on `dispose`. */
export interface Staged {
  dispose(): void;
}

/**
 * Runs `build` and remembers what it added to the scene: meshes, nodes,
 * materials, textures, lights and particles. The arena's views are built this
 * way, so a change of map (D49) takes the old arena out without each view
 * having to know how to dismantle itself. Nothing else may be built meanwhile:
 * `build` is synchronous.
 */
export function stage(scene: Scene, build: () => void): Staged {
  const before = {
    meshes: new Set(scene.meshes),
    nodes: new Set(scene.transformNodes),
    materials: new Set(scene.materials),
    textures: new Set(scene.textures),
    lights: new Set(scene.lights),
    particles: new Set(scene.particleSystems),
  };
  build();
  const meshes = scene.meshes.filter((m) => !before.meshes.has(m));
  const nodes = scene.transformNodes.filter((n) => !before.nodes.has(n));
  const materials = scene.materials.filter((m) => !before.materials.has(m));
  // Render targets stay: the depth renderer behind the depth of field is the scene's, made once and
  // reused by the next arena's post-processing (the shadow map goes with its generator).
  const textures = scene.textures.filter((t) => !before.textures.has(t) && !t.isRenderTarget);
  const lights = scene.lights.filter((l) => !before.lights.has(l));
  const particles = scene.particleSystems.filter((p) => !before.particles.has(p));
  return {
    dispose() {
      for (const p of particles) p.dispose(false);
      for (const m of meshes) if (!m.isDisposed()) m.dispose(false, false);
      for (const n of nodes) if (!n.isDisposed()) n.dispose(false, false);
      for (const m of materials) m.dispose(false, false);
      for (const t of textures) t.dispose();
      for (const l of lights) l.dispose();
    },
  };
}
